// StudentRegistration.js
import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate, Link as RouterLink } from 'react-router-dom';
import axios from 'axios';
import backendURL from '../../config';
import brandingBg from '../../assets/images/bot-avatar.jpg';

import {
  Box, Typography, TextField, Button, Alert, CircularProgress,
  Checkbox, FormControlLabel, Link as MuiLink, useTheme,
  LinearProgress,
} from '@mui/material';
import SchoolOutlinedIcon from '@mui/icons-material/SchoolOutlined';
import PersonOutlineIcon from '@mui/icons-material/PersonOutline';
import EmailOutlinedIcon from '@mui/icons-material/EmailOutlined';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import BoltIcon from '@mui/icons-material/Bolt';
import ShieldOutlinedIcon from '@mui/icons-material/ShieldOutlined';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import MenuBookOutlinedIcon from '@mui/icons-material/MenuBookOutlined';
import BarChartOutlinedIcon from '@mui/icons-material/BarChartOutlined';
import WorkspacePremiumOutlinedIcon from '@mui/icons-material/WorkspacePremiumOutlined';
import StarOutlineIcon from '@mui/icons-material/StarOutline';
import SignalCellularAltIcon from '@mui/icons-material/SignalCellularAlt';

const colors = {
  sidebarBg: '#1e293b',
  sidebarHover: '#313d4a',
  sidebarText: '#dee4ee',
  sidebarTextMuted: '#8a99af',
  accent: '#3b82f6',
  bgPrimary: '#f8fafc',
  bgMuted: '#f1f5f9',
  border: '#e2e8f0',
  textPrimary: '#1e293b',
  textSecondary: '#475569',
  textTertiary: '#94a3b8',
  success: '#059669',
  danger: '#dc2626',
};

const fontFamily = "'Plus Jakarta Sans', -apple-system, BlinkMacSystemFont, sans-serif";

const strengthColors = { weak: '#dc2626', medium: '#f59e0b', strong: '#059669' };

const getPasswordStrength = (password) => {
  if (password.length < 6) return { strength: 'weak', text: 'Too short', percent: 33 };
  let score = 0;
  if (password.length >= 8) score++;
  if (password.length >= 12) score++;
  if (/[a-z]/.test(password) && /[A-Z]/.test(password)) score++;
  if (/\d/.test(password)) score++;
  if (/[^a-zA-Z0-9]/.test(password)) score++;
  if (score <= 2) return { strength: 'weak', text: 'Weak password', percent: 33 };
  if (score <= 3) return { strength: 'medium', text: 'Medium strength', percent: 66 };
  return { strength: 'strong', text: 'Strong password', percent: 100 };
};

const formatDifficulty = (level) => {
  const map = { beginner: 'Beginner', intermediate: 'Intermediate', advanced: 'Advanced', expert: 'Expert' };
  return map[level?.toLowerCase()] || level || 'All Levels';
};

// KlonIT Logo SVG
const KlonITLogo = ({ color = "white", size = 24 }) => (
  <svg width={size} height={size} viewBox="0 0 32 32" fill="none">
    <path d="M8 6L16 16L8 26" stroke={color} strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" />
    <path d="M16 6L24 16L16 26" stroke={color} strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" opacity="0.6" />
  </svg>
);

const FeatureCard = ({ icon, title, description }) => (
  <Box sx={{
    display: 'flex', alignItems: 'center', gap: 2,
    p: '1rem 1.25rem', bgcolor: colors.sidebarHover,
    border: '1px solid rgba(255,255,255,0.05)', borderRadius: '12px',
    transition: 'all 0.25s ease',
    '&:hover': { bgcolor: 'rgba(59,130,246,0.1)', borderColor: 'rgba(59,130,246,0.2)', transform: 'translateX(4px)' },
  }}>
    <Box sx={{
      width: 42, height: 42, bgcolor: 'rgba(59,130,246,0.15)', borderRadius: '10px',
      display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, color: colors.accent,
    }}>
      {icon}
    </Box>
    <Box>
      <Typography sx={{ fontSize: '0.875rem', fontWeight: 600, color: colors.sidebarText, mb: 0.25 }}>{title}</Typography>
      <Typography sx={{ fontSize: '0.875rem', color: colors.sidebarTextMuted, lineHeight: 1.5 }}>{description}</Typography>
    </Box>
  </Box>
);

const CourseDetailsCard = ({ course }) => (
  <Box sx={{
    background: 'linear-gradient(135deg, #eff6ff 0%, #f0f7ff 100%)',
    border: `1px solid ${colors.accent}30`,
    borderRadius: '14px',
    p: 2, mb: 2,
  }}>
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1.5 }}>
      <Box sx={{
        width: 44, height: 44, borderRadius: '10px',
        background: `linear-gradient(135deg, ${colors.accent}, #2563eb)`,
        display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
        overflow: 'hidden',
      }}>
        {course.thumbnail_url ? (
          <img src={course.thumbnail_url} alt={course.title} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
        ) : (
          <SchoolOutlinedIcon sx={{ color: '#fff', fontSize: 22 }} />
        )}
      </Box>
      <Box sx={{ minWidth: 0 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.25 }}>
          <BoltIcon sx={{ fontSize: 11, color: colors.accent }} />
          <Typography sx={{ fontSize: '0.6875rem', fontWeight: 600, color: colors.accent, fontFamily, textTransform: 'uppercase', letterSpacing: '0.04em' }}>
            Enrolling In
          </Typography>
        </Box>
        <Typography sx={{ fontSize: '0.9375rem', fontWeight: 700, color: colors.textPrimary, fontFamily, lineHeight: 1.3, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {course.title}
        </Typography>
        {course.category && (
          <Typography sx={{ fontSize: '0.75rem', color: colors.textSecondary, fontFamily }}>{course.category}</Typography>
        )}
      </Box>
    </Box>
    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75 }}>
      <Box sx={{
        display: 'inline-flex', alignItems: 'center', gap: 0.5,
        px: 1, py: 0.25, borderRadius: '6px', fontSize: '0.6875rem', fontWeight: 600, fontFamily,
        bgcolor: 'rgba(59,130,246,0.1)', color: colors.accent,
      }}>
        <SignalCellularAltIcon sx={{ fontSize: 11 }} />
        {formatDifficulty(course.difficulty_level)}
      </Box>
      {course.completion_points > 0 && (
        <Box sx={{
          display: 'inline-flex', alignItems: 'center', gap: 0.5,
          px: 1, py: 0.25, borderRadius: '6px', fontSize: '0.6875rem', fontWeight: 600, fontFamily,
          bgcolor: 'rgba(245,158,11,0.1)', color: '#d97706',
        }}>
          <StarOutlineIcon sx={{ fontSize: 11 }} />
          {course.completion_points} Points
        </Box>
      )}
      {course.is_certificate_enabled && (
        <Box sx={{
          display: 'inline-flex', alignItems: 'center', gap: 0.5,
          px: 1, py: 0.25, borderRadius: '6px', fontSize: '0.6875rem', fontWeight: 600, fontFamily,
          bgcolor: 'rgba(5,150,105,0.1)', color: colors.success,
        }}>
          <WorkspacePremiumOutlinedIcon sx={{ fontSize: 11 }} />
          Certificate
        </Box>
      )}
    </Box>
  </Box>
);

function StudentRegistrationInner() {
  const theme = useTheme();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const adminAuthToken = searchParams.get('admin-auth');
  const enrollCourseIdentity = searchParams.get('enroll-course');

  // Page state: 'validating' | 'invalid' | 'form' | 'success'
  const [pageState, setPageState] = useState('validating');
  const [courseInfo, setCourseInfo] = useState(null);

  // Form fields
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [acceptedTerms, setAcceptedTerms] = useState(false);

  // UI state
  const [errors, setErrors] = useState({});
  const [apiError, setApiError] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [successData, setSuccessData] = useState(null);

  useEffect(() => {
    document.title = 'Student Registration | KlonIT';

    const validateToken = async () => {
      if (!adminAuthToken) {
        setPageState('invalid');
        return;
      }
      try {
        const response = await axios.post(
          `${backendURL}/api/lms/portal/students/validate-token`,
          { admin_auth: adminAuthToken, course_identity: enrollCourseIdentity }
        );
        if (response.data.success) {
          setCourseInfo(response.data.data?.course || null);
          setPageState('form');
        } else {
          setPageState('invalid');
        }
      } catch (error) {
        if (error.response?.status === 404) {
          setPageState('form');
        } else if ([401, 403, 422].includes(error.response?.status)) {
          setPageState('invalid');
        } else {
          setPageState('form');
        }
      }
    };

    validateToken();
  }, [adminAuthToken, enrollCourseIdentity]);

  useEffect(() => {
    if (pageState === 'success') {
      const timer = setTimeout(() => navigate('/Login'), 3000);
      return () => clearTimeout(timer);
    }
  }, [pageState, navigate]);

  const validateForm = () => {
    const newErrors = {};
    if (!firstName.trim()) newErrors.firstName = 'First name is required';
    else if (firstName.trim().length < 2) newErrors.firstName = 'Must be at least 2 characters';

    if (!lastName.trim()) newErrors.lastName = 'Last name is required';
    else if (lastName.trim().length < 2) newErrors.lastName = 'Must be at least 2 characters';

    if (!email) newErrors.email = 'Email is required';
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) newErrors.email = 'Invalid email address';

    if (!password) newErrors.password = 'Password is required';
    else if (password.length < 8) newErrors.password = 'Must be at least 8 characters';

    if (!confirmPassword) newErrors.confirmPassword = 'Please confirm your password';
    else if (password !== confirmPassword) newErrors.confirmPassword = 'Passwords do not match';

    if (!acceptedTerms) newErrors.terms = 'You must accept the terms';

    return newErrors;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setApiError(null);

    const validationErrors = validateForm();
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      return;
    }

    setErrors({});
    setIsSubmitting(true);

    try {
      const response = await axios.post(
        `${backendURL}/api/lms/portal/students/registration`,
        {
          first_name: firstName.trim(),
          last_name: lastName.trim(),
          email: email.trim().toLowerCase(),
          password,
          admin_auth: adminAuthToken,
          course_identity: enrollCourseIdentity,
        }
      );

      const responseData = typeof response.data.data === 'string'
        ? JSON.parse(response.data.data)
        : response.data.data;

      setSuccessData({
        studentName: `${firstName} ${lastName}`,
        email,
        courseTitle: responseData?.course?.title || courseInfo?.title,
      });
      setPageState('success');
    } catch (err) {
      setApiError(
        err.response?.data?.message ||
        'Registration failed. Please try again or contact support.'
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  const passwordStrength = password ? getPasswordStrength(password) : null;

  const inputSx = {
    '& .MuiOutlinedInput-root': {
      borderRadius: '14px', fontFamily, bgcolor: 'rgba(255,255,255,0.7)', backdropFilter: 'blur(8px)',
      border: 'none', outline: 'none',
      '& .MuiOutlinedInput-notchedOutline': { display: 'none !important' },
      '& fieldset': { display: 'none !important' },
      '&.Mui-focused': { bgcolor: 'rgba(255,255,255,1)', boxShadow: `0 0 0 2px ${theme.palette.primary.main}40` },
      '&:hover': { bgcolor: 'rgba(255,255,255,0.9)' },
    },
    '& .MuiInputBase-input': {
      fontFamily, fontSize: '0.875rem', py: '0.75rem', px: '0.5rem',
      border: 'none', outline: 'none',
      '&:focus': { outline: 'none', boxShadow: 'none' },
      '&::placeholder': { color: colors.textTertiary, opacity: 1 },
    },
    '& .MuiInputAdornment-root': { color: colors.textTertiary },
    '& .MuiInputLabel-root': { fontFamily },
    '& .MuiTextField-root': { border: 'none', outline: 'none' },
    '& .MuiFormHelperText-root': { fontFamily, fontSize: '0.75rem', mx: 0.5 },
  };

  // --- Render States ---

  const renderValidating = () => (
    <Box sx={{ textAlign: 'center', py: 6 }}>
      <CircularProgress size={40} sx={{ color: colors.accent, mb: 2 }} />
      <Typography sx={{ fontSize: '1.125rem', fontWeight: 600, color: colors.textPrimary, fontFamily, mb: 0.5 }}>
        Validating Registration Link
      </Typography>
      <Typography sx={{ fontSize: '0.8125rem', color: colors.textTertiary, fontFamily }}>
        Please wait while we verify your access...
      </Typography>
    </Box>
  );

  const renderInvalid = () => (
    <Box sx={{ textAlign: 'center', py: 4 }}>
      <Box sx={{
        width: 64, height: 64, borderRadius: '50%', mx: 'auto', mb: 2,
        bgcolor: 'rgba(220,38,38,0.1)', display: 'flex', alignItems: 'center', justifyContent: 'center',
      }}>
        <ErrorOutlineIcon sx={{ fontSize: 32, color: colors.danger }} />
      </Box>
      <Typography sx={{ fontSize: '1.25rem', fontWeight: 700, color: colors.textPrimary, fontFamily, mb: 1 }}>
        Invalid Registration Link
      </Typography>
      <Typography sx={{ fontSize: '0.875rem', color: colors.textSecondary, fontFamily, mb: 3, lineHeight: 1.6 }}>
        This registration link is invalid or has expired.<br />
        Please contact your administrator for a new link.
      </Typography>
      <Button
        component={RouterLink} to="/Login"
        sx={{
          py: '0.625rem', px: 3, fontFamily, fontSize: '0.875rem', fontWeight: 600, textTransform: 'none',
          background: `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.primary.dark})`,
          color: '#fff', borderRadius: '12px',
          '&:hover': { background: `linear-gradient(135deg, ${theme.palette.primary.dark}, ${theme.palette.primary.main})` },
        }}
      >
        Back to Login
      </Button>
    </Box>
  );

  const renderSuccess = () => (
    <Box sx={{ textAlign: 'center', py: 4 }}>
      <Box sx={{
        width: 64, height: 64, borderRadius: '50%', mx: 'auto', mb: 2,
        bgcolor: 'rgba(5,150,105,0.1)', display: 'flex', alignItems: 'center', justifyContent: 'center',
      }}>
        <CheckCircleOutlineIcon sx={{ fontSize: 32, color: colors.success }} />
      </Box>
      <Typography sx={{ fontSize: '1.25rem', fontWeight: 700, color: colors.textPrimary, fontFamily, mb: 0.5 }}>
        Registration Successful!
      </Typography>
      <Typography sx={{ fontSize: '0.875rem', color: colors.textSecondary, fontFamily, mb: 2.5, lineHeight: 1.6 }}>
        Your student account has been created. You can now sign in to access your courses.
      </Typography>

      {successData && (
        <Box sx={{
          bgcolor: 'rgba(5,150,105,0.05)', border: `1px solid rgba(5,150,105,0.15)`,
          borderRadius: '12px', p: 2, mb: 2.5, textAlign: 'left',
        }}>
          {[
            { label: 'Name', value: successData.studentName },
            { label: 'Email', value: successData.email },
            ...(successData.courseTitle ? [{ label: 'Enrolled Course', value: successData.courseTitle }] : []),
          ].map((item) => (
            <Box key={item.label} sx={{ display: 'flex', justifyContent: 'space-between', py: 0.75, '&:not(:last-child)': { borderBottom: `1px solid rgba(5,150,105,0.1)` } }}>
              <Typography sx={{ fontSize: '0.8125rem', color: colors.textTertiary, fontFamily }}>{item.label}</Typography>
              <Typography sx={{ fontSize: '0.8125rem', fontWeight: 600, color: colors.textPrimary, fontFamily }}>{item.value}</Typography>
            </Box>
          ))}
        </Box>
      )}

      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1, mb: 1.5 }}>
        <CircularProgress size={14} sx={{ color: colors.textTertiary }} />
        <Typography sx={{ fontSize: '0.8125rem', color: colors.textTertiary, fontFamily }}>
          Redirecting to login...
        </Typography>
      </Box>

      <Typography sx={{ fontSize: '0.75rem', color: colors.textTertiary, fontFamily }}>
        Not redirecting?{' '}
        <MuiLink component={RouterLink} to="/Login" sx={{ color: colors.accent, fontWeight: 600, fontFamily }}>
          Click here to sign in
        </MuiLink>
      </Typography>
    </Box>
  );

  const renderForm = () => (
    <form onSubmit={handleSubmit} noValidate>
      {courseInfo && <CourseDetailsCard course={courseInfo} />}

      {apiError && (
        <Alert severity="error" sx={{ mb: 1.5, borderRadius: '12px', fontFamily, fontSize: '0.8125rem', '& .MuiAlert-message': { fontFamily } }}>
          {apiError}
        </Alert>
      )}

      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
        {/* Name fields row */}
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 1 }}>
          <TextField
            fullWidth size="small" placeholder="First name"
            value={firstName} onChange={(e) => { setFirstName(e.target.value); setErrors(prev => ({ ...prev, firstName: undefined })); }}
            error={!!errors.firstName} helperText={errors.firstName}
            disabled={isSubmitting} autoComplete="given-name"
            InputProps={{ startAdornment: <PersonOutlineIcon sx={{ fontSize: 18, color: colors.textTertiary, mr: 0.75 }} /> }}
            sx={inputSx}
          />
          <TextField
            fullWidth size="small" placeholder="Last name"
            value={lastName} onChange={(e) => { setLastName(e.target.value); setErrors(prev => ({ ...prev, lastName: undefined })); }}
            error={!!errors.lastName} helperText={errors.lastName}
            disabled={isSubmitting} autoComplete="family-name"
            InputProps={{ startAdornment: <PersonOutlineIcon sx={{ fontSize: 18, color: colors.textTertiary, mr: 0.75 }} /> }}
            sx={inputSx}
          />
        </Box>

        {/* Email */}
        <TextField
          fullWidth size="small" type="email" placeholder="Email address"
          value={email} onChange={(e) => { setEmail(e.target.value); setErrors(prev => ({ ...prev, email: undefined })); }}
          error={!!errors.email} helperText={errors.email}
          disabled={isSubmitting} autoComplete="email"
          InputProps={{ startAdornment: <EmailOutlinedIcon sx={{ fontSize: 18, color: colors.textTertiary, mr: 0.75 }} /> }}
          sx={inputSx}
        />

        {/* Password */}
        <Box>
          <TextField
            fullWidth size="small" type="password" placeholder="Create a strong password"
            value={password} onChange={(e) => { setPassword(e.target.value); setErrors(prev => ({ ...prev, password: undefined })); }}
            error={!!errors.password} helperText={errors.password}
            disabled={isSubmitting} autoComplete="new-password"
            InputProps={{ startAdornment: <LockOutlinedIcon sx={{ fontSize: 18, color: colors.textTertiary, mr: 0.75 }} /> }}
            sx={inputSx}
          />
          {password && passwordStrength && (
            <Box sx={{ mt: 0.5, px: 0.5 }}>
              <LinearProgress
                variant="determinate" value={passwordStrength.percent}
                sx={{
                  height: 4, borderRadius: 2, bgcolor: colors.border,
                  '& .MuiLinearProgress-bar': { bgcolor: strengthColors[passwordStrength.strength], borderRadius: 2 },
                }}
              />
              <Typography sx={{ fontSize: '0.6875rem', color: strengthColors[passwordStrength.strength], mt: 0.25, fontFamily, fontWeight: 500 }}>
                {passwordStrength.text}
              </Typography>
            </Box>
          )}
        </Box>

        {/* Confirm Password */}
        <TextField
          fullWidth size="small" type="password" placeholder="Confirm your password"
          value={confirmPassword} onChange={(e) => { setConfirmPassword(e.target.value); setErrors(prev => ({ ...prev, confirmPassword: undefined })); }}
          error={!!errors.confirmPassword} helperText={errors.confirmPassword}
          disabled={isSubmitting} autoComplete="new-password"
          InputProps={{ startAdornment: <LockOutlinedIcon sx={{ fontSize: 18, color: colors.textTertiary, mr: 0.75 }} /> }}
          sx={inputSx}
        />
      </Box>

      {/* Terms */}
      <FormControlLabel
        control={
          <Checkbox
            checked={acceptedTerms}
            onChange={(e) => { setAcceptedTerms(e.target.checked); setErrors(prev => ({ ...prev, terms: undefined })); }}
            size="small" disabled={isSubmitting}
            sx={{ color: colors.textTertiary, '&.Mui-checked': { color: colors.accent }, pt: 0 }}
          />
        }
        label={
          <Typography sx={{ fontSize: '0.8125rem', color: colors.textSecondary, fontFamily, lineHeight: 1.4 }}>
            I agree to the{' '}
            <MuiLink href="/terms" target="_blank" sx={{ color: colors.accent, fontWeight: 500, fontFamily }}>Terms of Service</MuiLink>
            {' '}and{' '}
            <MuiLink href="/privacy" target="_blank" sx={{ color: colors.accent, fontWeight: 500, fontFamily }}>Privacy Policy</MuiLink>
          </Typography>
        }
        sx={{ alignItems: 'flex-start', mx: 0, mt: 1, mb: 0.5 }}
      />
      {errors.terms && (
        <Typography sx={{ fontSize: '0.75rem', color: colors.danger, fontFamily, ml: 0.5, mt: -0.25, mb: 0.5 }}>
          {errors.terms}
        </Typography>
      )}

      {/* Submit */}
      <Button
        type="submit" fullWidth disabled={isSubmitting}
        sx={{
          py: '0.75rem', fontFamily, fontSize: '0.9375rem', fontWeight: 600, textTransform: 'none',
          background: `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.primary.dark})`,
          color: '#fff', borderRadius: '14px', height: 46, gap: 1,
          boxShadow: `0 4px 16px ${theme.palette.primary.main}40`,
          transition: 'all 0.25s ease',
          '&:hover': {
            background: `linear-gradient(135deg, ${theme.palette.primary.dark}, ${theme.palette.primary.main})`,
            boxShadow: `0 6px 20px ${theme.palette.primary.main}50`,
            transform: 'translateY(-1px)',
          },
          '&:disabled': { opacity: 0.7 },
        }}
      >
        {isSubmitting && <CircularProgress size={18} sx={{ color: '#fff' }} />}
        {isSubmitting ? 'Creating Account...' : 'Create Student Account'}
      </Button>

      {/* Footer */}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 2, mt: 2 }}>
        <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5, fontSize: '0.6875rem', color: colors.textTertiary, fontFamily }}>
          <ShieldOutlinedIcon sx={{ fontSize: 13, color: colors.success }} />
          Secure
        </Box>
        <Box sx={{ width: 3, height: 3, borderRadius: '50%', bgcolor: colors.textTertiary }} />
        <Typography sx={{ fontSize: '0.8125rem', color: colors.textTertiary, fontFamily }}>
          Already registered?{' '}
          <MuiLink component={RouterLink} to="/Login" sx={{ color: theme.palette.primary.main, fontWeight: 600, fontFamily }}>Sign in</MuiLink>
        </Typography>
      </Box>
    </form>
  );

  return (
    <>
      <style>{`@import url('https://fonts.googleapis.com/css2?family=Plus+Jakarta+Sans:ital,wght@0,200..800;1,200..800&display=swap');`}</style>

      <Box sx={{ fontFamily, minHeight: '100vh', bgcolor: colors.bgPrimary, display: 'flex' }}>
        {/* Left Branding Panel */}
        <Box sx={{
          display: { xs: 'none', lg: 'flex' }, width: '50%',
          flexDirection: 'column', justifyContent: 'center', p: '4rem', position: 'relative', overflow: 'hidden',
          backgroundImage: `linear-gradient(rgba(15, 23, 42, 0.85), rgba(15, 23, 42, 0.9)), url(${brandingBg})`,
          backgroundSize: 'cover', backgroundPosition: 'center',
          '&::before': {
            content: '""', position: 'absolute', inset: 0, pointerEvents: 'none',
            background: 'radial-gradient(circle at 20% 30%, rgba(59,130,246,0.12) 0%, transparent 40%), radial-gradient(circle at 80% 70%, rgba(59,130,246,0.08) 0%, transparent 40%)',
          },
        }}>
          <Box sx={{ position: 'relative', zIndex: 1, maxWidth: 480, mx: 'auto' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: '0.875rem', mb: 3 }}>
              <Box sx={{
                width: 48, height: 48, bgcolor: colors.accent, borderRadius: '12px',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                boxShadow: '0 8px 24px rgba(59,130,246,0.3)',
              }}>
                <KlonITLogo />
              </Box>
              <Typography sx={{ fontSize: '1.75rem', fontWeight: 700, color: '#fff', letterSpacing: '-0.025em', fontFamily }}>KlonIT</Typography>
            </Box>

            <Box sx={{
              display: 'inline-flex', alignItems: 'center', gap: 0.5,
              px: 2, py: 0.5, bgcolor: colors.sidebarHover,
              border: '1px solid rgba(255,255,255,0.08)', borderRadius: '100px',
              fontSize: '0.8125rem', fontWeight: 600, color: colors.accent, mb: 2, letterSpacing: '0.05em',
            }}>
              <BoltIcon sx={{ fontSize: 14 }} />
              LEARN · GROW · SUCCEED
            </Box>

            <Typography sx={{ fontSize: '2.75rem', fontWeight: 700, lineHeight: 1.15, letterSpacing: '-0.03em', color: '#fff', mb: 1.5, fontFamily }}>
              Start Your<br /><Box component="span" sx={{ color: colors.accent }}>Learning Journey</Box>
            </Typography>

            <Typography sx={{ fontSize: '1rem', lineHeight: 1.7, color: colors.sidebarTextMuted, mb: 2.5, fontFamily }}>
              Create your student account to access world-class courses, AI-powered tutoring, and personalized learning paths.
            </Typography>

            <Box sx={{ display: 'flex', flexDirection: 'column', gap: '0.875rem' }}>
              <FeatureCard icon={<MenuBookOutlinedIcon sx={{ fontSize: 20 }} />} title="Expert-Led Courses" description="Learn from industry professionals and thought leaders" />
              <FeatureCard icon={<BarChartOutlinedIcon sx={{ fontSize: 20 }} />} title="Track Your Progress" description="Visual dashboards and achievement milestones" />
              <FeatureCard icon={<WorkspacePremiumOutlinedIcon sx={{ fontSize: 20 }} />} title="Earn Certificates" description="Get recognized for your accomplishments" />
            </Box>
          </Box>
        </Box>

        {/* Right Form Panel */}
        <Box sx={{
          flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center',
          p: { xs: 2, lg: '3rem 4rem' }, position: 'relative', overflow: 'hidden',
          background: `linear-gradient(135deg, #f8fafc 0%, #eef2ff 40%, #e0e7ff 70%, #f0f4ff 100%)`,
          '&::before': {
            content: '""', position: 'absolute', top: '-30%', right: '-20%',
            width: '500px', height: '500px', borderRadius: '50%', pointerEvents: 'none',
            background: `radial-gradient(circle, ${theme.palette.primary.main}12 0%, transparent 70%)`,
          },
          '&::after': {
            content: '""', position: 'absolute', bottom: '-20%', left: '-10%',
            width: '400px', height: '400px', borderRadius: '50%', pointerEvents: 'none',
            background: 'radial-gradient(circle, rgba(139,92,246,0.08) 0%, transparent 70%)',
          },
        }}>
          <Box sx={{ width: '100%', maxWidth: 440, position: 'relative', zIndex: 1 }}>
            {/* Mobile Logo */}
            <Box sx={{ display: { xs: 'flex', lg: 'none' }, alignItems: 'center', justifyContent: 'center', gap: '0.75rem', mb: 2 }}>
              <Box sx={{ width: 44, height: 44, bgcolor: colors.sidebarBg, borderRadius: '12px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <KlonITLogo color="white" />
              </Box>
              <Typography sx={{ fontSize: '1.5rem', fontWeight: 700, color: colors.textPrimary, letterSpacing: '-0.025em', fontFamily }}>KlonIT</Typography>
            </Box>

            {/* Header */}
            <Box sx={{ textAlign: 'center', mb: 2 }}>
              <Box sx={{
                width: 48, height: 48, borderRadius: '14px', mx: 'auto', mb: 1.5,
                background: `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.primary.dark})`,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                boxShadow: `0 8px 24px ${theme.palette.primary.main}30`,
              }}>
                <SchoolOutlinedIcon sx={{ color: '#fff', fontSize: 24 }} />
              </Box>
              <Typography sx={{ fontSize: '1.5rem', fontWeight: 700, color: colors.textPrimary, letterSpacing: '-0.02em', mb: 0.25, fontFamily }}>
                Create Your Account
              </Typography>
              <Typography sx={{ fontSize: '0.8125rem', color: colors.textTertiary, fontFamily }}>
                Register to start your learning journey
              </Typography>
            </Box>

            {/* Dynamic content based on page state */}
            {pageState === 'validating' && renderValidating()}
            {pageState === 'invalid' && renderInvalid()}
            {pageState === 'form' && renderForm()}
            {pageState === 'success' && renderSuccess()}
          </Box>
        </Box>
      </Box>
    </>
  );
}

export default function StudentRegistration() {
  return <StudentRegistrationInner />;
}
